import { normalizeGuideTemplate } from '@/lib/print/tenantGuideTemplateEngine.js';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const BASE_URL = `${API_BASE_URL}/api/guias-print-config`;

function buildHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('token');
    const tenantRaw = localStorage.getItem('tenant');
    if (token) headers.Authorization = `Bearer ${token}`;
    if (tenantRaw) {
      try {
        const tenant = JSON.parse(tenantRaw);
        headers['X-Tenant'] = tenant?.code || tenant?.domain || tenantRaw;
      } catch {
        headers['X-Tenant'] = tenantRaw;
      }
    }
  } catch {
    // noop
  }
  return headers;
}

async function parseResponse(response, fallbackMessage) {
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || payload?.message || fallbackMessage);
  }
  return payload?.data ?? null;
}

async function fileToBase64(file) {
  const buffer = await file.arrayBuffer();
  let binary = '';
  new Uint8Array(buffer).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export async function fetchGuidePrintTemplate(slug = '') {
  const query = slug ? `?slug=${encodeURIComponent(slug)}` : '';
  const response = await fetch(`${BASE_URL}${query}`, {
    headers: buildHeaders(),
    credentials: 'include',
  });
  return normalizeGuideTemplate(await parseResponse(response, 'No se pudo obtener la plantilla.'));
}

export async function fetchGuidePrintTemplates() {
  const response = await fetch(`${BASE_URL}/list`, {
    headers: buildHeaders(),
    credentials: 'include',
  });
  const data = await parseResponse(response, 'No se pudieron obtener las plantillas.');
  return Array.isArray(data) ? data.map((item) => normalizeGuideTemplate(item)) : [];
}

export async function saveGuidePrintTemplate(input) {
  const response = await fetch(BASE_URL, {
    method: 'PUT',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify(input),
  });
  return normalizeGuideTemplate(await parseResponse(response, 'No se pudo guardar la plantilla.'));
}

export async function duplicateGuidePrintTemplate(slug) {
  const response = await fetch(`${BASE_URL}/duplicate`, {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ slug }),
  });
  return normalizeGuideTemplate(await parseResponse(response, 'No se pudo duplicar la plantilla.'));
}

export async function restoreGuidePrintTemplate(modeImpresion, presetLayout = 'clasica') {
  const response = await fetch(`${BASE_URL}/restore-system`, {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ modeImpresion, presetLayout }),
  });
  return normalizeGuideTemplate(await parseResponse(response, 'No se pudo restaurar la base.'));
}

export async function importGuidePrintTemplateFromLegacy({ legacyVariant, reText = '', active = true, presetLayout = 'clasica' }) {
  const response = await fetch(`${BASE_URL}/import-vb`, {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ legacyVariant, reText, active, presetLayout }),
  });
  return normalizeGuideTemplate(await parseResponse(response, 'No se pudo importar la plantilla legacy.'));
}

export async function fetchGuidePrintLogo() {
  const response = await fetch(`${BASE_URL}/logo`, {
    headers: buildHeaders(),
    credentials: 'include',
  });
  return parseResponse(response, 'No se pudo obtener el logo.');
}

export async function uploadGuidePrintLogo(file) {
  const response = await fetch(`${BASE_URL}/logo`, {
    method: 'PUT',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      fileName: file.name,
      mimeType: file.type,
      base64: await fileToBase64(file),
    }),
  });
  return parseResponse(response, 'No se pudo guardar el logo.');
}

export async function deleteGuidePrintLogo() {
  const response = await fetch(`${BASE_URL}/logo`, {
    method: 'DELETE',
    headers: buildHeaders(),
    credentials: 'include',
  });
  return parseResponse(response, 'No se pudo eliminar el logo.');
}

export async function fetchGuidePrintBackground() {
  const response = await fetch(`${BASE_URL}/background`, {
    headers: buildHeaders(),
    credentials: 'include',
  });
  return parseResponse(response, 'No se pudo obtener el PDF de fondo.');
}

export async function uploadGuidePrintBackground(file) {
  const response = await fetch(`${BASE_URL}/background`, {
    method: 'PUT',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      fileName: file.name,
      mimeType: file.type,
      base64: await fileToBase64(file),
    }),
  });
  return parseResponse(response, 'No se pudo guardar el PDF de fondo.');
}

export async function deleteGuidePrintBackground() {
  const response = await fetch(`${BASE_URL}/background`, {
    method: 'DELETE',
    headers: buildHeaders(),
    credentials: 'include',
  });
  return parseResponse(response, 'No se pudo eliminar el PDF de fondo.');
}

export async function previewGuidePrintTemplate(template, guideId, signal) {
  const response = await fetch(BASE_URL + '/preview', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ template, guideId }),
    signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || 'No se pudo generar la vista previa.');
  }
  return response.blob();
}